import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, FolderTree, Image, ShoppingCart, Users, TicketPercent, FileText, Target, Bot, ShieldCheck, Bell, Menu, X, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../store/Header";

export const NAV = [
  ["/admin", "Dashboard", LayoutDashboard, null],
  ["/admin/products", "Products", Package, "products"],
  ["/admin/categories", "Categories", FolderTree, "products"],
  ["/admin/media", "Media Library", Image, "products"],
  ["/admin/orders", "Orders", ShoppingCart, "orders"],
  ["/admin/customers", "Customers", Users, "customers"],
  ["/admin/coupons", "Coupons", TicketPercent, "coupons"],
  ["/admin/content", "Website Content", FileText, "content"],
  ["/admin/leads", "CRM · Leads", Target, "leads"],
  ["/admin/chatbot", "AI Chatbot", Bot, "chatbot"],
  ["/admin/inbox", "Inbox & Alerts", Bell, null],
  ["/admin/team", "Admin Users", ShieldCheck, "super"],
];

function Side({ onNav }) {
  const { user, can, logout } = useAuth();
  const items = NAV.filter(([, , , p]) => !p || (p === "super" ? user.role === "super_admin" : can(p)));
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-[#E8E0D5]"><Logo /></div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {items.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} end={to === "/admin"} onClick={onNav} data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-[#1B3B2B] text-[#FBF8F3]" : "text-stone-700 hover:bg-[#E2EBE2]"}`}>
            <Icon className="w-4 h-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#E8E0D5] space-y-1 text-sm">
        <p className="px-3 text-xs text-stone-500 truncate">{user.email} · {user.role === "super_admin" ? "Super Admin" : "Staff"}</p>
        <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#E2EBE2]" data-testid="admin-view-store"><ExternalLink className="w-4 h-4" /> View store</Link>
        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-[#E2EBE2]" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Log out</button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-sm">Loading…</div>;
  if (!user) return <Navigate to={`/admin/login?next=${loc.pathname}`} replace />;
  if (!isAdmin) return <div className="min-h-screen grid place-items-center text-center p-6"><div><h1 className="text-3xl text-[#1B3B2B]">Admin access only</h1><Link to="/" className="underline text-sm">Back to store</Link></div></div>;
  return (
    <div className="min-h-screen bg-[#F7F4EE] lg:grid lg:grid-cols-[250px_1fr]">
      <aside className="hidden lg:block sticky top-0 h-screen bg-[#FFFDF9] border-r border-[#E8E0D5]"><Side /></aside>
      {open && <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} /><aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#FFFDF9] fade-up"><Side onNav={() => setOpen(false)} /></aside></div>}
      <div className="min-w-0">
        <header className="lg:hidden sticky top-0 z-30 h-14 bg-[#FFFDF9]/90 backdrop-blur border-b border-[#E8E0D5] flex items-center justify-between px-4">
          <button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu" aria-label="Menu">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          <span className="font-serif text-lg text-[#1B3B2B]">SEED Admin</span><span className="w-5" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px]"><Outlet /></main>
      </div>
    </div>
  );
}
